import type { ExpandedState, SortingState, Updater } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useShallow } from "zustand/react/shallow"

import type { TransferSearchParams } from "../schemas/transfer"
import { useTransferSearchStore } from "../stores/useTransferSearchStore"

import type { Transfer } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UseTransferListReturn {
  transfers: Transfer[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  expanded: ExpandedState
  searchParams: TransferSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleScroll: () => void
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleExpandedChange: (updater: Updater<ExpandedState>) => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof TransferSearchParams>(key: K, value: TransferSearchParams[K]) => void
  handleFilterRemove: (
    keyOrKeys: keyof TransferSearchParams | (keyof TransferSearchParams)[],
    value?: string | number
  ) => void
  handleFilterClear: () => void
}

const ROUTE = "/app/inventory/_layout/transfers"

const FILTER_KEYS: (keyof TransferSearchParams)[] = [
  "types",
  "statuses",
  "sourceWarehouseAreaIds",
  "targetWarehouseAreaIds",
  "createdByUserIds",
  "createdOnFrom",
  "createdOnTo",
  "productCostCompleted",
]

export const useTransferList = (): UseTransferListReturn => {
  const store = useTransferSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const {
    data: transfers,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleExpandedChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  } = useListPageBase<Transfer, TransferSearchParams>({
    route: ROUTE,
    store,
    filterKeys: FILTER_KEYS,
  })

  return {
    transfers,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleExpandedChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
